import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, signal } from '@angular/core';
import { forkJoin } from 'rxjs';
import { ContextService } from 'src/app/core/service/api/context.service';
import { StakeholdersService } from 'src/app/core/service/api/stakeholders.service';
import { CachedService } from 'src/app/core/service/common/cached.service';
import { showFeedbackSaved } from 'src/app/core/util/notification';
import { getPhysicalContextLabel } from 'src/app/core/constants/featureLabels';
import { SubprojectQuery } from 'src/app/akita/subproject/state/subprojects.query';

@Component({
  selector: 'app-individual-context-detail',
  templateUrl: './individual-context-detail.component.html',
  styleUrl: './individual-context-detail.component.sass',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class IndividualContextDetailComponent implements OnInit {
  @Input() contextItemDetail: any = null;
  @Output() onItemSelection: EventEmitter<any> = new EventEmitter<any>();
  @Output() onClose: EventEmitter<any> = new EventEmitter<any>();
  @Output() onHoverContext: EventEmitter<{type: string, item: any, is_subtype: boolean}> = new EventEmitter<{type: string, item: any, is_subtype: boolean}>();
  @Output() onLeaveContext: EventEmitter<void> = new EventEmitter<void>();
  isProcessing = signal<boolean>(false);
  isSharedSubproject = signal<boolean>(false);
  isLoadingExistingContext = signal<boolean>(false); // Global loading state for fetching existing context details
  showDetail: boolean = false;
  processingItemId = signal<string | null>(null);
  pendingContextLoads = 0; // Track number of pending context load requests
  issues: any = [];
  stakeholders: any = [];
  contexts: any = [];
  selectedStakeholders: any = [];
  selectedIssues: any = [];
  selectedStakeholdersMap: { [uuid: string]: any[] } = {};
  selectedIssuesMap: { [uuid: string]: any[] } = {};
  selectedContextsMap: { [uuid: string]: any[] } = {};
  addedMap: { [uuid: string]: boolean } = {};
  loadingContextMap: { [uuid: string]: boolean } = {}; // Track loading state for each context by UUID
  createdContexts : any = [];
  createdStakeholders : any = [];
  contextTypes = [];
  expandedItemUuid: string | null = null;
  formattedTags: Array<{ key: string; label: string; value: string }> = [];
  stkkeys: any;
  stkvalues: any;
  
  // For add-connect-generic component
  entities: any;
  loaded: boolean = false;

  constructor(private _contextsService: ContextService, private subProjectQuery: SubprojectQuery, private _stakeholdersService: StakeholdersService, private cachedService: CachedService, private cdRef: ChangeDetectorRef) {}

  // Methods to get translated tag labels and values for template
  getTagLabel(key: string): string {
    return getPhysicalContextLabel(key);
  }

  ngOnInit(): void {
    const activeSubProject = this.subProjectQuery.getActive();
    this.isSharedSubproject.set(activeSubProject && activeSubProject?.tenant_type === 'shared');
    this._contextsService.getContextTypes().subscribe((res)=>{
    this.contextTypes = res;
    })
    const tags = this.contextItemDetail?.item?.contexts?.[0]?.metadata?.tags ?? {};
    this.stkkeys = Object.keys(tags);
    this.stkvalues = Object.values(tags);
    // Pre-populate data for manual items (already in project)
    this.prePopulateManualItems();
    
    // Set loading state immediately if items have UUIDs that might match (prevents flash)
    if (this.hasItemsNeedingContextCheck()) {
      this.isLoadingExistingContext.set(true);
    }
    
    this.getRelations();
  }

  /**
   * Pre-populates data for items that are already marked as manual (in project)
   */
  private prePopulateManualItems(): void {
    if (!this.contextItemDetail?.item) return;
    
    // For Stakeholder type, use name as key
    if (this.contextItemDetail?.type === 'Stakeholder') {
      const item = this.contextItemDetail.item;
      if (item.isManual && item.name) {
        const key = item.uuid || item.name;
        this.addedMap[key] = true;
        this.selectedContextsMap[key] = item.contexts ?? [];
        if (!this.createdStakeholders.find(stk => stk.name === item.name)) {
          this.createdStakeholders.push(item);
        }
      }
      return;
    }
    
    // Pre-populate main item if manual
    if (this.contextItemDetail.item.isManual) {
      const item = this.contextItemDetail.item;
      const uuid = item.uuid;
      if (uuid) {
        this.addedMap[uuid] = true;
        this.selectedStakeholdersMap[uuid] = item.stakeholders ?? [];
        this.selectedIssuesMap[uuid] = item.issues ?? [];
        this.selectedContextsMap[uuid] = item.contexts ?? [];
        // Ensure arrays are initialized on the item
        item.stakeholders = item.stakeholders ?? [];
        item.issues = item.issues ?? [];
        if (!this.createdContexts.find(ctx => ctx.uuid === uuid)) {
          this.createdContexts.push(item);
        }
      }
    }
    
    // Pre-populate context_items if they are manual
    if (Array.isArray(this.contextItemDetail.item.context_items)) {
      this.contextItemDetail.item.context_items.forEach((item: any) => {
        if (item.isManual && item.uuid) {
          this.addedMap[item.uuid] = true;
          this.selectedStakeholdersMap[item.uuid] = item.stakeholders ?? [];
          this.selectedIssuesMap[item.uuid] = item.issues ?? [];
          this.selectedContextsMap[item.uuid] = item.contexts ?? [];
          // Ensure arrays are initialized on the item
          item.stakeholders = item.stakeholders ?? [];
          item.issues = item.issues ?? [];
          if (!this.createdContexts.find(ctx => ctx.uuid === item.uuid)) {
            this.createdContexts.push(item);
          }
        }
      });
    }
  }

  /**
   * Checks if any items need to be checked against existing data.
   * For Stakeholder type: checks by name
   * For other types: checks by UUID
   * Returns true only for non-manual items (manual items already have data).
   */
  private hasItemsNeedingContextCheck(): boolean {
    if (!this.contextItemDetail?.item) return false;
    
    // For Stakeholder type, check by name
    if (this.contextItemDetail?.type === 'Stakeholder') {
      return !this.contextItemDetail.item.isManual && !!this.contextItemDetail.item.name;
    }
    
    // Check main item - only if not manual and has UUID
    if (!this.contextItemDetail.item.isManual && this.contextItemDetail.item.uuid) {
      return true;
    }
    
    // Check context_items - only non-manual items with UUIDs
    if (Array.isArray(this.contextItemDetail.item.context_items)) {
      return this.contextItemDetail.item.context_items.some(
        (item: any) => !item.isManual && item.uuid
      );
    }
    
    return false;
  }
  
  toggleDetail() {
    this.showDetail = !this.showDetail;
  }

  onHoverContextItem(type: string, item: any) {
    this.onHoverContext.emit({ type, item, is_subtype: false });
  }
  
  onLeaveContextItem() {
    this.onLeaveContext.emit();
  }

  getRelations(): void {
    this.loaded = false;
    forkJoin({
      issues: this.cachedService.getIssuesRelations(),
      stakeholders: this.cachedService.getStakeholdersRelations(),
      contexts: this.cachedService.getContextsRelations()
    })
    .subscribe({
      next: ({issues, stakeholders, contexts}) => {
        this.issues = issues;
        this.stakeholders = stakeholders;
        this.contexts = contexts;
        
        // Build entities object for add-connect-generic component
        this.entities = {
          stakeholders: this.stakeholders,
          issues: this.issues
        };
        
        this.loaded = true;
        
        // Check if the current context item is already added to the project by comparing UUIDs
        this.checkIfContextAlreadyAdded();
      },
      error: (err) => console.error("Error in forkJoin:", err)
    });
  }

  /**
   * Checks if the context item is already added to the project
   * For Stakeholder type: checks by name
   * For other types: checks by UUID
   * If found, fetches the full details and updates the component state
   * Also checks context_items if they exist
   * Skips items that are already manual (they already have data loaded)
   */
  private checkIfContextAlreadyAdded(): void {
    if (!this.contextItemDetail?.item) {
      // No items to check, stop loading
      this.isLoadingExistingContext.set(false);
      this.cdRef.detectChanges();
      return;
    }

    // For Stakeholder type, use name-based checking
    if (this.contextItemDetail?.type === 'Stakeholder') {
      this.checkIfStakeholderAlreadyAdded();
      return;
    }

    // Get contexts list from cached service
    const contextsList = Array.isArray(this.contexts) 
      ? this.contexts 
      : (this.contexts?.data || []);

    // Collect all NON-MANUAL items that need to be checked
    // Manual items already have data loaded, so we skip them
    const itemsToCheck: any[] = [];
    
    // Add main context item only if not manual
    if (!this.contextItemDetail.item.isManual) {
      itemsToCheck.push(this.contextItemDetail.item);
    }

    // Add context_items if they exist and are not manual
    if (Array.isArray(this.contextItemDetail.item.context_items)) {
      const nonManualItems = this.contextItemDetail.item.context_items.filter(
        (item: any) => !item.isManual
      );
      itemsToCheck.push(...nonManualItems);
    }

    // Check which items have matching UUIDs and need to be loaded
    const itemsToLoad = itemsToCheck.filter(item => {
      const itemUuid = item.uuid || this.getItemUuid(item);
      if (!itemUuid) return false;
      const existingContext = contextsList.find((ctx: any) => ctx.uuid === itemUuid);
      return existingContext && existingContext.id;
    });

    // Set pending loads count
    this.pendingContextLoads = itemsToLoad.length;
    
    // If no items to load, stop loading state immediately
    if (this.pendingContextLoads === 0) {
      this.isLoadingExistingContext.set(false);
      this.cdRef.detectChanges();
      return;
    }

    // Load each matching item
    itemsToLoad.forEach((item: any) => {
      this.checkAndLoadContext(item, contextsList);
    });
  }

  /**
   * Checks if the stakeholder is already added to the project by comparing names
   * If found, fetches the full stakeholder details via getStakeholder API
   */
  private checkIfStakeholderAlreadyAdded(): void {
    const stakeholderItem = this.contextItemDetail?.item;
    
    if (!stakeholderItem || stakeholderItem.isManual) {
      // No item or already manual, stop loading
      this.isLoadingExistingContext.set(false);
      this.cdRef.detectChanges();
      return;
    }

    // Get stakeholders list from cached service
    const stakeholdersList = Array.isArray(this.stakeholders) 
      ? this.stakeholders 
      : (this.stakeholders?.data || []);

    const stakeholderName = stakeholderItem.name;
    
    if (!stakeholderName) {
      // No name to match against
      this.isLoadingExistingContext.set(false);
      this.cdRef.detectChanges();
      return;
    }

    // Find existing stakeholder by name (case-insensitive comparison)
    const existingStakeholder = stakeholdersList.find(
      (stk: any) => stk.name?.toLowerCase() === stakeholderName.toLowerCase()
    );

    if (existingStakeholder && (existingStakeholder.id || existingStakeholder.code)) {
      // Stakeholder exists, load full details
      this.pendingContextLoads = 1;
      this.checkAndLoadStakeholder(stakeholderItem, existingStakeholder);
    } else {
      // No matching stakeholder found
      this.isLoadingExistingContext.set(false);
      this.cdRef.detectChanges();
    }
  }

  /**
   * Fetches full stakeholder details using getStakeholder API
   * @param stakeholderItem - The stakeholder item from the analysis
   * @param existingStakeholder - The matching stakeholder from cached list
   */
  private checkAndLoadStakeholder(stakeholderItem: any, existingStakeholder: any): void {
    const stakeholderId = existingStakeholder.code || existingStakeholder.id;
    const stakeholderName = stakeholderItem.name;

    // Set loading state for this stakeholder
    this.loadingContextMap[stakeholderName] = true;

    // Fetch full stakeholder details
    this._stakeholdersService.getStakeholderSummary(stakeholderId).subscribe({
      next: (fullStakeholder: any) => {
        // Transform the response to match the expected format
        const transformedStakeholder = this.transformStakeholderToItemDetailFormat(fullStakeholder, stakeholderItem);
        
        // Update component state using stakeholder name as key
        const uuid = fullStakeholder.uuid || stakeholderItem.uuid || stakeholderName;
        this.addedMap[uuid] = true;
        this.selectedContextsMap[uuid] = fullStakeholder.contexts || [];
        
        // Add to createdStakeholders if not already present
        const existingCreated = this.createdStakeholders.find(stk => stk.name === stakeholderName);
        if (!existingCreated) {
          this.createdStakeholders.push(transformedStakeholder);
        } else {
          // Update existing stakeholder with full details
          const index = this.createdStakeholders.indexOf(existingCreated);
          this.createdStakeholders[index] = transformedStakeholder;
        }

        // Update the stakeholderItem with the full data
        Object.assign(stakeholderItem, transformedStakeholder);

        // Clear loading state for this item
        this.loadingContextMap[stakeholderName] = false;
        
        // Decrement pending loads and check if all done
        this.onContextLoadComplete();
      },
      error: (err) => {
        console.error("Error fetching existing stakeholder:", err);
        // Clear loading state on error
        this.loadingContextMap[stakeholderName] = false;
        
        // Decrement pending loads and check if all done
        this.onContextLoadComplete();
      }
    });
  }

  /**
   * Transforms the getStakeholder response to match the contextItemDetail format
   * @param stakeholder - The stakeholder object from getStakeholder API
   * @param originalItem - The original stakeholder item from analysis
   * @returns Transformed stakeholder object matching contextItemDetail format
   */
  private transformStakeholderToItemDetailFormat(stakeholder: any, originalItem: any): any {
    return {
      id: stakeholder.id,
      code: stakeholder.code,
      uuid: stakeholder.uuid || originalItem.uuid,
      name: stakeholder.name,
      address: stakeholder.address || originalItem.address,
      location: stakeholder.location || originalItem.location,
      locations: stakeholder.locations || originalItem.locations || [],
      contexts: stakeholder.contexts || [],
      issues: stakeholder.issues || [],
      stakeholders: stakeholder.stakeholders || [],
      metadata: stakeholder.metadata || {},
      isManual: true, // Mark as manual since it's already in the project
      ...stakeholder // Include any other properties
    };
  }

  /**
   * Checks if a specific context item exists by UUID and loads its full details if found
   * @param contextItem - The context item to check
   * @param contextsList - List of cached contexts to search
   */
  private checkAndLoadContext(contextItem: any, contextsList: any[]): void {
    const itemUuid = contextItem.uuid || this.getItemUuid(contextItem);

    if (!itemUuid) {
      return; // No UUID to match against
    }

    const existingContext = contextsList.find((ctx: any) => ctx.uuid === itemUuid);

    if (existingContext && existingContext.id) {
      // Set loading state for this context
      this.loadingContextMap[itemUuid] = true;

      // Context already exists, fetch full details
      this._contextsService.getContext(existingContext.id).subscribe({
        next: (fullContext) => {
          // Transform the response to match contextItemDetail format
          const transformedContext = this.transformContextToItemDetailFormat(fullContext);
          
          // Update component state
          const uuid = fullContext.uuid || itemUuid;
          this.addedMap[uuid] = true;
          this.selectedStakeholdersMap[uuid] = fullContext.stakeholders || [];
          this.selectedIssuesMap[uuid] = fullContext.issues || [];
          this.selectedContextsMap[uuid] = fullContext.contexts || [];
          
          // Add to createdContexts if not already present
          const existingCreated = this.createdContexts.find(ctx => ctx.uuid === uuid);
          if (!existingCreated) {
            this.createdContexts.push(transformedContext);
          } else {
            // Update existing context with full details
            const index = this.createdContexts.indexOf(existingCreated);
            this.createdContexts[index] = transformedContext;
          }

          // Update the contextItem with the full context data
          Object.assign(contextItem, transformedContext);
          // Sync with maps
          this.syncContextItemWithMaps(contextItem);

          // Clear loading state for this item
          this.loadingContextMap[itemUuid] = false;
          
          // Decrement pending loads and check if all done
          this.onContextLoadComplete();
        },
        error: (err) => {
          console.error("Error fetching existing context:", err);
          // Clear loading state on error
          this.loadingContextMap[itemUuid] = false;
          
          // Decrement pending loads and check if all done
          this.onContextLoadComplete();
        }
      });
    }
  }

  /**
   * Called when a context load completes (success or error)
   * Decrements pending count and hides global loader when all done
   */
  private onContextLoadComplete(): void {
    this.pendingContextLoads--;
    if (this.pendingContextLoads <= 0) {
      this.pendingContextLoads = 0;
      this.isLoadingExistingContext.set(false);
    }
    this.cdRef.detectChanges();
  }

  /**
   * Checks if a context item is currently loading its details
   * @param item - The context item to check
   * @returns True if the context is loading
   */
  isContextLoading(item: any): boolean {
    if (!item) return false;
    const uuid = item.uuid || this.getItemUuid(item);
    return this.loadingContextMap[uuid] === true;
  }

  /**
   * Transforms the getContext response to match the contextItemDetail format
   * @param context - The context object from getContext API
   * @returns Transformed context object matching contextItemDetail format
   */
  private transformContextToItemDetailFormat(context: any): any {
    return {
      id: context.id,
      uuid: context.uuid,
      text: context.text,
      name: context.text, // Some components use name instead of text
      description: context.description,
      stakeholders: context.stakeholders || [],
      issues: context.issues || [],
      contexts: context.contexts || [],
      types: context.types || [],
      locations: context.locations || [],
      metadata: context.metadata || {},
      is_relevant: context.is_relevant,
      isManual: true, // Mark as manual since it's already in the project
      ...context // Include any other properties
    };
  }

  goBack(): void {
    this.onClose.emit();
  }

  addToProject(context: any, type: 'add' | 'remove'): void {
    // Handle both direct context object and wrapped object from template (data.item)
    const contextItem = context?.item || context;
    // Always resolve the key/UUID through the helper so it stays consistent with maps & template
    const uuid = this.getItemUuid(contextItem);
    this.isProcessing.set(true);
    this.processingItemId.set(uuid); // Always use UUID for consistency

    const {id,...data} = contextItem;
    const contextTypeName = this.contextItemDetail?.type?.toLowerCase();
    const matchedType = this.contextTypes.find(ct => ct.name.toLowerCase() === contextTypeName);

    if (type=='add' && this.contextItemDetail?.type != 'Stakeholder') {
      // Include uuid in payload - use the one from API response, don't generate a new one
      const payload = { uuid, ...data, types: matchedType ? [matchedType] : [] };
      this._contextsService.createContext(payload).subscribe({
        next: (createdContext) => {
          this.createdContexts.push(createdContext);
          // Use UUID as the key for maps - use createdContext UUID if available, otherwise use original uuid
          const contextUuid = createdContext.uuid || uuid;
          
          // IMPORTANT: Update the original contextItem's uuid to match createdContext.uuid
          // This ensures the template can access the map using data.item.uuid
          if (contextItem && !contextItem.isManual && contextItem !== createdContext) {
            contextItem.uuid = contextUuid;
          }
          // Also update contextItemDetail.item if it exists
          if (this.contextItemDetail?.item && this.contextItemDetail.item !== createdContext && !this.contextItemDetail.item.isManual) {
            this.contextItemDetail.item.uuid = contextUuid;
          }
          
          this.addedMap[contextUuid] = true;
          this.selectedStakeholdersMap[contextUuid] = [];
          this.selectedIssuesMap[contextUuid] = [];
          // Ensure arrays are initialized on the created context
          createdContext.stakeholders = [];
          createdContext.issues = [];
          // Sync the context item with maps
          this.syncContextItemWithMaps(createdContext);
          // Also sync the original contextItem if it exists
          if (contextItem && !contextItem.isManual) {
            contextItem.stakeholders = [];
            contextItem.issues = [];
            this.syncContextItemWithMaps(contextItem);
          }
          // Trigger change detection to show spinner and update UI
          this.cdRef.detectChanges();
          
          // Check if there are stakeholders to add
          const stakeholders = data.stakeholders || (data.item && data.item.stakeholders) || [];
          if (stakeholders.length > 0) {
            const stakeholder = stakeholders[0];
            const stakeholderPayload = { 
              name: stakeholder.name, 
              locations: stakeholder.locations || (stakeholder.location ? [stakeholder.location] : []), 
              address: stakeholder.address 
            };
            
            this._stakeholdersService.submitStakeholder(stakeholderPayload).subscribe({
              next: (createdStakeholder) => {
                this.createdStakeholders.push(createdStakeholder);
                // Update the selectedStakeholdersMap using contextUuid as key
                this.selectedStakeholdersMap[contextUuid] = [createdStakeholder];
                // Make sure we're using the correct reference - update createdContext reference
                const updatedContext = this.createdContexts.find(ctx => ctx.uuid === contextUuid);
                if (updatedContext) {
                  updatedContext.stakeholders = [createdStakeholder];
                }

                // Update the original contextItem as well
                if (contextItem && contextItem !== createdContext) {
                  contextItem.stakeholders = [createdStakeholder];
                }
                
                // Also update contextItemDetail.item if it's the main item
                if (this.contextItemDetail?.item && this.contextItemDetail.item.uuid === contextUuid) {
                  this.contextItemDetail.item.stakeholders = [createdStakeholder];
                }
                // Update the context with the stakeholder connection - use createdContext which has the UUID
                this.updateContextConnections(createdContext, [createdStakeholder], 'stakeholders');
                // Trigger change detection to update UI and show selected chips
                this.cdRef.detectChanges();
              },
              error: (err) => {
                console.error("Add Stakeholder API error:", err);
                this.isProcessing.set(false);
                this.cdRef.detectChanges();
              },
              complete: () => {
                this.getRelations();
                this.isProcessing.set(false);
                showFeedbackSaved();
                this.cdRef.detectChanges();
              }
            });
          } else {
            // No stakeholders to add, just complete
            this.isProcessing.set(false);
            showFeedbackSaved();
            this.cdRef.detectChanges();
          }
        },
        error: (err) => {
          console.error("Add Context API error:", err);
          this.isProcessing.set(false);
        }
      });
    }
    else if (type=='add' && this.contextItemDetail?.type == 'Stakeholder') {
      // For stakeholders, use name as the key since we match by name
      const stakeholderName = data.item?.name || data.name;
      const stakeholderUuid = (data.item?.uuid || data.uuid) || stakeholderName;
      const payload = { uuid: stakeholderUuid, name: stakeholderName, locations: [data.item?.location || data.location], address: data.item?.address || data.address };
      this._stakeholdersService.submitStakeholder(payload).subscribe({
        next: (res) => {
          this.createdStakeholders.push(res);
          // Use UUID from response if available, otherwise use name as key
          const stakeholderKey = res.uuid || stakeholderUuid;
          const stakeholderCode = res.code || res.id;
          this.addedMap[stakeholderKey] = true;
          
          // Update the original item's uuid to match the response
          if (contextItem && !contextItem.isManual) {
            contextItem.uuid = stakeholderKey;
          }
          if (this.contextItemDetail?.item && !this.contextItemDetail.item.isManual) {
            this.contextItemDetail.item.uuid = stakeholderKey;
          }
          
          const contextTypeName = data.contexts?.[0]?.context_type?.toLowerCase();
          const matchedType = this.contextTypes.find(ct => ct.name.toLowerCase() === contextTypeName);
          
          // Only create context if there are contexts to create
          if (data.contexts && data.contexts.length > 0) {
            // Use UUID from context data if available, otherwise use stakeholder UUID
            const contextUuid = data.contexts[0]?.uuid || stakeholderUuid;
            const contextPayload = { uuid: contextUuid, ...data.contexts[0], types: matchedType ? [matchedType] : [] };
            this._contextsService.createContext(contextPayload).subscribe({
              next: (createdContext) => {
                this.createdContexts.push(createdContext);
                this.addedMap[contextUuid] = true;
                this.selectedContextsMap[stakeholderKey] = [createdContext];
                // Make sure the newly created context is available in the options list
                this.contexts = [...this.contexts, createdContext];
                
                // Update the stakeholder with the context connection
                this.updateStakeholderConnections(res, [createdContext], 'contexts');
                this.cdRef.detectChanges();
              },
            });
          } else {
            // No contexts to create, initialize empty
            this.selectedContextsMap[stakeholderKey] = [];
          }
          this.cdRef.detectChanges();
        },
        error: (err) => {
          console.error("Add API error:", err);
          this.isProcessing.set(false);
        },
        complete: () => {
          this.isProcessing.set(false);
          showFeedbackSaved();
        }
      });
    }
    else if (type=='remove') {
      // When working with Stakeholders, we shouldn't look in createdContexts – they live in createdStakeholders
      if (this.contextItemDetail?.type === 'Stakeholder') {
        const stakeholderKey = this.getItemUuid(contextItem);
        const stakeholderItem = this.createdStakeholders.find(stk =>
          this.getItemUuid(stk) === stakeholderKey || stk.name === contextItem.name
        );

        if (!stakeholderItem) {
          console.warn('Stakeholder not found in createdStakeholders:', stakeholderKey);
          this.addedMap[stakeholderKey] = false;
          this.selectedContextsMap[stakeholderKey] = [];
          this.isProcessing.set(false);
          return;
        }

        this._stakeholdersService.removeStakeholders(stakeholderItem).subscribe({
          next: () => {
            this.addedMap[stakeholderKey] = false;
            this.selectedContextsMap[stakeholderKey] = [];
            this.createdStakeholders = this.createdStakeholders.filter(
              stk => this.getItemUuid(stk) !== stakeholderKey
            );
            showFeedbackSaved();
          },
          error: (err) => {
            console.error('Delete Stakeholder API error:', err);
          },
          complete: () => {
            this.isProcessing.set(false);
          }
        });
      } else {
        // Non-stakeholder remove: delete the context itself
        const item = this.createdContexts.find(ctx => ctx.uuid === uuid);

        if (!item) {
          console.error("Item not found in createdContexts:", uuid);
          this.isProcessing.set(false);
          return;
        }

        this._contextsService.deleteContext(item.id).subscribe({
          next: () => {
            this.addedMap[uuid] = false;
            this.selectedStakeholdersMap[uuid] = [];
            this.selectedIssuesMap[uuid] = [];
            this.createdContexts = this.createdContexts.filter(ctx => ctx.uuid !== uuid);
            showFeedbackSaved();
          },
          error: (err) => console.error("Delete API error:", err),
          complete: () => this.isProcessing.set(false)
        });
      }
    }
  }

  isArrayOfStrings(value: any): boolean {
    if (!Array.isArray(value)) {
      return false;
    }
    return value.length > 0 && value.every(item => typeof item === 'string');
  }

  /**
   * Generates a UUID as a fallback when the API doesn't provide one.
   * This should only be used for items that don't have a uuid from the API response.
   * @param context - The context item
   * @returns A generated UUID string (fallback only)
   */
  generateUuid(context) {
    const {id,...data} = context;
    return (id + '-' + (data.text?.replace(/ /g, '-') ?? data.name?.replace(/ /g, '-') ?? ''));
  }

  getItemUuid(item: any): string {
    // Helper method to get UUID/key from item (for use in template)
    if (!item) return '';
    
    // For Stakeholder type, use name as the key (since we match by name, not UUID)
    if (this.contextItemDetail?.type === 'Stakeholder' && item.name) {
      // Priority 1: Check if stakeholder has a UUID
      if (item.uuid) return item.uuid;
      // Priority 2: Check createdStakeholders by name
      const foundStakeholder = this.createdStakeholders.find(stk => stk.name === item.name);
      if (foundStakeholder?.uuid) return foundStakeholder.uuid;
      // Priority 3: Use name as fallback key for stakeholders
      return item.name;
    }
    
    // Priority 1: Use UUID from API response (most important - this is what we want to preserve)
    if (item.uuid) {
      return item.uuid;
    }
    // Priority 2: Check if it's in createdContexts and use that UUID
    const foundContext = this.createdContexts.find(ctx => 
      (ctx.id && ctx.id === item.id) || 
      (ctx.text === item.text && ctx.name === item.name)
    );
    if (foundContext?.uuid) {
      return foundContext.uuid;
    }
    // Priority 3: Fallback to generating UUID only if no UUID exists (should rarely happen)
    return item.isManual ? (item.uuid || '') : this.generateUuid(item);
  }

  updateContextConnections(contextItem: any, items: any, type: 'issues' | 'stakeholders' | 'contexts'): void {
    const uuid = contextItem.uuid || this.generateUuid(contextItem);
    const item = this.createdContexts.find(ctx => ctx.uuid === uuid);
    if (!item) {
      console.warn('Context not found in createdContexts for UUID:', uuid);
      return;
    }

    if (type === 'stakeholders') {
      // Use UUID as key instead of id
      this.selectedStakeholdersMap[uuid] = items;
      item.stakeholders = items;
    } else if (type === 'issues') {
      // Use UUID as key instead of id
      this.selectedIssuesMap[uuid] = items;
      item.issues = items;
    }
    else if (type === 'contexts') {
      // Context selection for stakeholders is client-side only (avoid cyclic payloads)
      const sanitized = (items || []).map((ctx: any) => ({
        id: ctx?.id,
        uuid: ctx?.uuid,
        text: ctx?.text,
        name: ctx?.name
      }));
      this.selectedContextsMap[uuid] = sanitized;
      item.contexts = sanitized;
      this.cdRef.detectChanges();
      return;
    }

    this._contextsService.updateContext(item.id, item).subscribe(res => {
      // Update the map with the response to ensure it's in sync
      if (type === 'stakeholders') {
        this.selectedStakeholdersMap[uuid] = res.stakeholders || items;
        item.stakeholders = res.stakeholders || items;
      
        // Update contextItem if it's different from item
        if (contextItem && contextItem !== item) {
          contextItem.stakeholders = res.stakeholders || items;
        }
        
        // Update contextItemDetail.item if it matches
        if (this.contextItemDetail?.item && this.contextItemDetail.item.uuid === uuid) {
          this.contextItemDetail.item.stakeholders = res.stakeholders || items;
        }
        this.stakeholders.push(...res.stakeholders);
      } else if (type === 'issues') {
        this.selectedIssuesMap[uuid] = res.issues || items;
      }
      // this.cdRef.detectChanges();
      this.cdRef.markForCheck();
      showFeedbackSaved();
    });
  }

  /**
   * Updates stakeholder connections (contexts) using the updateStakeholder API
   * First fetches the full stakeholder data, updates contexts, then sends full payload
   * @param stakeholderItem - The stakeholder item to update
   * @param items - The contexts to connect
   * @param type - The type of connection ('contexts')
   */
  updateStakeholderConnections(stakeholderItem: any, items: any, type: 'contexts'): void {
    const stakeholderKey = stakeholderItem.uuid || stakeholderItem.name;
    let stakeholderCode = stakeholderItem.code || stakeholderItem.id;
    
    // Find the stakeholder in createdStakeholders
    const item = this.createdStakeholders.find(stk => 
      stk.code === stakeholderCode || stk.id === stakeholderCode || stk.name === stakeholderItem.name
    );
    
    if (!item) {
      console.warn('Stakeholder not found in createdStakeholders:', stakeholderKey);
      return;
    }

    stakeholderCode = stakeholderCode ? stakeholderCode : item.code || item.id;

    if (type === 'contexts') {
      // Sanitize context items for the payload
      const sanitized = (items || []).map((ctx: any) => ({
        id: ctx?.id,
        uuid: ctx?.uuid,
        text: ctx?.text,
        name: ctx?.name
      }));
      this.selectedContextsMap[stakeholderKey] = sanitized;
      item.contexts = sanitized;

      // First get the full stakeholder data, then update with contexts
      this._stakeholdersService.getStakeholderSummary(stakeholderCode).subscribe({
        next: (fullStakeholder: any) => {
          // Use the full stakeholder response as the payload, with updated contexts
          const payload = {
            ...fullStakeholder,
            contexts: items
          };
          
          // Update the stakeholder via API with full payload
          this._stakeholdersService.updateStakeholder(stakeholderCode, payload).subscribe({
            next: (res: any) => {
              // Update the map with the response to ensure it's in sync
              this.selectedContextsMap[stakeholderKey] = res.contexts || sanitized;
              // Update the stakeholder in createdStakeholders
              const index = this.createdStakeholders.indexOf(item);
              if (index !== -1) {
                this.createdStakeholders[index] = { ...item, ...res, contexts: res.contexts || sanitized };
              }
              this.cdRef.detectChanges();
              showFeedbackSaved();
            },
            error: (err) => {
              console.error('Error updating stakeholder connections:', err);
            }
          });
        },
        error: (err) => {
          console.error('Error fetching stakeholder for update:', err);
        }
      });
    }
  }

  hasValidThemes(item: any): boolean {
    const themes = item?.parameters?.[0]?.source_data?.themes;
    if (!Array.isArray(themes)) return false;

    return themes.some(t =>
      t?.name !== undefined &&
      t?.summary !== undefined &&
      t?.evidence !== undefined
    );
  }

  
  updateEntitiesFromGeneric(event: any, contextItem: any): void {
    const uuid = this.getItemUuid(contextItem);
    
    // Update the contextItem directly first (this is what relation-overview is bound to)
    if (event.type === 'stakeholders') {
      // Create new array reference to ensure change detection
      contextItem.stakeholders = event.selected ? [...event.selected] : [];
      // Update the maps
      this.selectedStakeholdersMap[uuid] = contextItem.stakeholders;
      // Then call updateContextConnections to sync with API
      this.updateContextConnections(contextItem, contextItem.stakeholders, 'stakeholders');
    } else if (event.type === 'issues') {
      // Create new array reference to ensure change detection
      contextItem.issues = event.selected ? [...event.selected] : [];
      // Update the maps
      this.selectedIssuesMap[uuid] = contextItem.issues;
      // Then call updateContextConnections to sync with API
      this.updateContextConnections(contextItem, contextItem.issues, 'issues');
    }
    
    // Trigger change detection to update the UI
    this.cdRef.detectChanges();
  }

  /**
   * Syncs the context item with the maps to ensure parent object has correct arrays
   * This should be called when the item is added to the project
   * @param contextItem - The context item to sync
   */
  syncContextItemWithMaps(contextItem: any): void {
    const uuid = this.getItemUuid(contextItem);
    // Ensure the item has the arrays initialized from maps
    if (!contextItem.stakeholders) {
      contextItem.stakeholders = [];
    }
    if (!contextItem.issues) {
      contextItem.issues = [];
    }
    // Sync with maps if they exist
    if (this.selectedStakeholdersMap[uuid]) {
      contextItem.stakeholders = this.selectedStakeholdersMap[uuid];
    }
    if (this.selectedIssuesMap[uuid]) {
      contextItem.issues = this.selectedIssuesMap[uuid];
    }
  }

  toggleItemExpansion(item: any, type?: string): void {
    const uuid = this.getItemUuid(item);
    
    if (this.expandedItemUuid === uuid) {
      this.expandedItemUuid = null;
      this.onItemSelection.emit(null);
    } else {
      this.expandedItemUuid = uuid;
      this.onItemSelection.emit({item, type});
    }
    
    this.cdRef.detectChanges();
  }

  isItemExpanded(item: any): boolean {
    const uuid = this.getItemUuid(item);
    return this.expandedItemUuid === uuid;
  }
  
  getSubthemeContextLabel(type: string, count: number): string {
    const isSingular = count <= 1;
      switch (type) {
        case 'Regulatory':
          return isSingular
            ? 'contexts.plan_in_subtheme'
            : 'contexts.plans_in_subtheme';
        case 'Political':
          return isSingular
            ? 'contexts.municipality_in_subtheme'
            : 'contexts.municipalities_in_subtheme';
        default:
          return isSingular
            ? 'contexts.neighborhood_in_subtheme'
            : 'contexts.neighborhoods_in_subtheme';
      }
  }
}

